import { useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd } from '@/components/JsonLd';
import { useT } from '@/i18n/provider';
import { SITE_ORIGIN, CONTACT_API_URL } from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

interface CardConfig {
  id: 'sales' | 'support' | 'security';
  email: string;
  hasExtra?: boolean;
}

const CONTACT_CARDS: CardConfig[] = [
  { id: 'sales', email: 'sales@logpulse.io' },
  { id: 'support', email: 'support@logpulse.io', hasExtra: true },
  { id: 'security', email: 'security@logpulse.io' },
];

const SUBJECT_KEYS = ['general', 'sales', 'support', 'partnership'] as const;

export function ContactPage() {
  const t = useT();

  useDocumentMeta({
    title: t('contact.meta.title'),
    description: t('contact.meta.description'),
  });

  const contactPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: t('contact.meta.title'),
    description: t('contact.meta.description'),
    url: `${SITE_ORIGIN}/contact`,
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
      contactPoint: [
        { '@type': 'ContactPoint', email: 'sales@logpulse.io', contactType: 'sales' },
        { '@type': 'ContactPoint', email: 'support@logpulse.io', contactType: 'customer support' },
        { '@type': 'ContactPoint', email: 'security@logpulse.io', contactType: 'security' },
      ],
    },
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
    company: '', // honeypot
  });
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;

    setStatus('submitting');
    setErrorMessage(null);
    trackEvent('contact_submit_attempt', { subject: formData.subject });

    try {
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setStatus('success');
      setFormData({ name: '', email: '', subject: 'general', message: '', company: '' });
      trackEvent('contact_submit_success', { subject: formData.subject });
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      trackEvent('contact_submit_error');
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <JsonLd data={contactPageJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('contact.hero.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CONTACT_CARDS.map((card) => (
            <div
              key={card.id}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t(`contact.cards.${card.id}.title`)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                {t(`contact.cards.${card.id}.description`)}
              </p>
              <a
                href={`mailto:${card.email}`}
                className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
              >
                {card.email}
              </a>
              {card.hasExtra && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                  <a
                    href="#"
                    className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
                  >
                    {t(`contact.cards.${card.id}.extra`)} &rarr;
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          {t('contact.form.title')}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-6 sm:p-8 space-y-5"
          noValidate
        >
          {/* Honeypot — bots fill, humans don't see */}
          <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
            <label htmlFor="company">Company (leave empty)</label>
            <input
              id="company"
              name="company"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.company}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                {t('contact.form.name')}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder={t('contact.form.namePlaceholder')}
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                {t('contact.form.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder={t('contact.form.emailPlaceholder')}
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
            >
              {t('contact.form.subject')}
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              disabled={status === 'submitting'}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {SUBJECT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`contact.form.subject.${key}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
            >
              {t('contact.form.message')}
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              required
              value={formData.message}
              onChange={handleChange}
              disabled={status === 'submitting'}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder={t('contact.form.messagePlaceholder')}
            />
          </div>

          {/* Status messages */}
          {status === 'success' && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-start gap-3 rounded-lg border border-success-500/40 bg-success-50 dark:bg-success-500/10 px-4 py-3 text-sm"
            >
              <CheckCircle2 className="h-5 w-5 text-success-600 dark:text-success-400 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-semibold text-success-600 dark:text-success-400">
                  {t('contact.form.successTitle')}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-0.5">
                  {t('contact.form.successBody')}
                </p>
              </div>
            </div>
          )}
          {status === 'error' && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border border-destructive-500/40 bg-destructive-500/5 dark:bg-destructive-500/10 px-4 py-3 text-sm"
            >
              <AlertCircle className="h-5 w-5 text-destructive-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-semibold text-destructive-600 dark:text-destructive-500">
                  {t('contact.form.errorTitle')}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-0.5">
                  {errorMessage ?? t('contact.form.errorBody')}
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('contact.form.submitting')}
              </>
            ) : (
              t('contact.form.submit')
            )}
          </button>
        </form>
      </section>

      {/* Office Info */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-sm mx-auto rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {t('contact.office.title')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            LogPulse B.V.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('contact.office.line2')}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          {t('contact.faq.title')}
        </h2>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-6"
            >
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                {t(`contact.faq.q${i}`)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {t(`contact.faq.a${i}`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
